import { uuidv7 } from '../../../utils/uuid';
import { isSafeUrl } from '../block-builder';
import type { GuideRenderContext, GuideStep } from '../types';

interface VeoBridgeMessage {
  __veo?: unknown;
  type?: unknown;
  url?: unknown;
  name?: unknown;
  props?: unknown;
  height?: unknown;
  close?: unknown;
}

/**
 * Monta el `<iframe sandbox>` con el HTML/CSS/JS del cliente (origen `null`,
 * sin acceso al host) y cablea el puente postMessage (`dismiss`/`cta`/`track`/
 * `navigate`/`resize`) validando `source` + token. Compartido por las guías
 * `custom` (flotante/anclada) e `inline-custom` (embebida en el flujo).
 *
 * El caller crea el wrapper y posiciona/inserta el iframe; aquí sólo va la
 * lógica del documento + el bridge. Devuelve el iframe y un cleanup.
 */
export function mountCustomFrame(
  doc: Document,
  step: GuideStep,
  context: GuideRenderContext,
  opts: { width: string; fixedHeight?: number | string | null },
): { iframe: HTMLIFrameElement; cleanup: () => void } {
  const iframe = doc.createElement('iframe');
  iframe.className = 'veo-custom-frame';
  // allow-scripts SIN allow-same-origin → origen null, sin acceso al host.
  iframe.setAttribute('sandbox', 'allow-scripts');
  iframe.setAttribute('title', context.guide.guideName || 'OnUser');
  iframe.style.width = opts.width;

  const hasFixedHeight = opts.fixedHeight !== undefined && opts.fixedHeight !== null;
  if (hasFixedHeight) iframe.style.height = toCssSize(opts.fixedHeight as number | string);

  const token = uuidv7();
  // fix: las apps modernas usan design tokens/Tailwind. Reenviamos las custom
  // properties del host (`:root`) al iframe para que el CSS del cliente pueda
  // usar `var(--su-token)`; y opcionalmente inyectamos Tailwind (Play CDN) si el
  // autor lo pidió en `style.tailwind` (sujeto al CSP de la app — ver más abajo).
  const wantsTailwind = (step.style as Record<string, unknown> | undefined)?.tailwind === true;
  iframe.srcdoc = buildSrcdoc(step.html ?? '', step.css ?? '', step.js ?? '', token, {
    hostCss: collectHostStyles(),
    htmlAttrs: hostHtmlAttrs(),
    tailwind: wantsTailwind,
  });

  const close = (): void => context.onClose();

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== iframe.contentWindow) return;
    const data = event.data as VeoBridgeMessage | null;
    if (!data || typeof data !== 'object' || data.__veo !== token) return;

    switch (data.type) {
      case 'dismiss':
        context.onInteraction({
          guideId: context.guide.guideId,
          stepIndex: 0,
          action: 'dismissed',
        });
        close();
        break;
      case 'cta':
        context.onInteraction({
          guideId: context.guide.guideId,
          stepIndex: 0,
          action: 'cta_clicked',
        });
        if (typeof data.url === 'string' && isSafeUrl(data.url)) {
          window.open(data.url, '_blank', 'noopener,noreferrer');
        }
        if (data.close !== false) close();
        break;
      case 'navigate':
        if (typeof data.url === 'string' && isSafeUrl(data.url)) {
          window.location.assign(data.url);
        }
        break;
      case 'track':
        if (typeof data.name === 'string' && context.onTrack) {
          context.onTrack(data.name, isRecord(data.props) ? data.props : undefined);
        }
        break;
      case 'resize':
        if (!hasFixedHeight && typeof data.height === 'number' && data.height > 0) {
          iframe.style.height = `${Math.ceil(data.height)}px`;
        }
        break;
    }
  };
  window.addEventListener('message', onMessage);

  return { iframe, cleanup: () => window.removeEventListener('message', onMessage) };
}

export function toCssSize(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value;
}

/** Tope del CSS del host que inyectamos (evita un srcdoc gigante). */
const MAX_HOST_CSS = 800 * 1024;

/**
 * Recolecta TODO el CSS legible del host (stylesheets + `<style>`) para
 * inyectarlo en el iframe de una guía custom. Así un HTML copiado desde la app
 * se ve IDÉNTICO: toma las utilidades de Tailwind COMPILADAS por la app (con su
 * config real — colores/radios custom como `text-brand`, `rounded-control`…) Y
 * las clases CSS propias (no-Tailwind), además de las variables de `:root`.
 *
 * Las hojas cross-origin (CDN de fuentes, etc.) lanzan `SecurityError` al leer
 * sus reglas y se saltan — no podemos copiarlas. Acotado a ~800KB.
 */
function collectHostStyles(): string {
  if (typeof document === 'undefined') return '';
  let out = '';
  try {
    for (const sheet of Array.from(document.styleSheets)) {
      let rules: CSSRuleList | null = null;
      try {
        rules = sheet.cssRules;
      } catch {
        continue; // hoja de otro origen: no legible
      }
      if (!rules) continue;
      for (const rule of Array.from(rules)) {
        out += `${rule.cssText}\n`;
        if (out.length > MAX_HOST_CSS) return out;
      }
    }
  } catch {
    return out;
  }
  return out;
}

/**
 * Copia atributos "de tema" del `<html>` del host al iframe: `class` (estrategia
 * de dark-mode por clase de Tailwind, p. ej. `dark`), `style` (variables CSS
 * seteadas inline en `:root`) y `data-theme`/`data-mode`. Sin esto, una guía
 * copiada de una app oscura renderizaría en claro (los tokens cambian según el
 * tema). Se sanitiza (sin `"`/`<`/`>`) porque va dentro de la etiqueta `<html>`.
 */
function hostHtmlAttrs(): string {
  if (typeof document === 'undefined') return '';
  try {
    const el = document.documentElement;
    const attrs: string[] = [];
    for (const name of ['class', 'style', 'data-theme', 'data-mode', 'data-color-mode']) {
      const v = el.getAttribute(name);
      if (v && v.length <= 1000 && !/["<>]/.test(v)) attrs.push(`${name}="${v}"`);
    }
    return attrs.join(' ');
  } catch {
    return '';
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Arma el documento del iframe. El JS del cliente puede llamar a `window.veo`
 * (bridge inyectado). `</script>`/`</style>` dentro del código se neutralizan
 * para que no corten sus bloques.
 *
 * `opts.hostCss` = CSS compilado del host (utilidades Tailwind reales + clases
 * propias + `:root`), para que el HTML copiado de la app se vea idéntico.
 * `opts.tailwind` inyecta ADEMÁS el Play CDN de Tailwind (para utilidades que la
 * app no usa y por ende no están en su CSS compilado) — sujeto al CSP de la app.
 */
function buildSrcdoc(
  html: string,
  css: string,
  js: string,
  token: string,
  opts: { hostCss: string; htmlAttrs: string; tailwind: boolean },
): string {
  const bridge =
    `(function(){var T=${JSON.stringify(token)};` +
    `function P(m){m.__veo=T;parent.postMessage(m,'*');}` +
    `window.veo={` +
    `dismiss:function(){P({type:'dismiss'});},` +
    `cta:function(u){P({type:'cta',url:u});},` +
    `track:function(n,p){P({type:'track',name:n,props:p||{}});},` +
    `navigate:function(u){P({type:'navigate',url:u});}};` +
    // Los <a href> dentro del iframe sandbox navegarían el iframe (la cajita),
    // no la app del host. Interceptamos el click y reenviamos la navegación al
    // host: links normales → navigate (misma pestaña); target=_blank → nueva
    // pestaña. Así los links de una guía custom "simplemente funcionan".
    `document.addEventListener('click',function(e){` +
    `var a=e.target&&e.target.closest?e.target.closest('a[href]'):null;` +
    `if(!a)return;` +
    `var h=a.getAttribute('href');` +
    `if(!h||h.charAt(0)==='#'||/^(javascript|mailto|tel):/i.test(h))return;` +
    `e.preventDefault();` +
    `if(a.target==='_blank'){P({type:'cta',url:a.href,close:false});}` +
    `else{P({type:'navigate',url:h});}` +
    `},true);` +
    // Medimos el alto del CONTENEDOR del contenido (`#__veo_root`), no el del
    // documento: el CSS de la app puede poner `body{min-height:100vh}` y estirar
    // el <html> a toda la pantalla (caja negra). El contenedor mide solo el
    // contenido real, así el iframe embebido se ajusta a lo que el autor definió.
    `function H(){var r=document.getElementById('__veo_root');` +
    `var h=r?r.getBoundingClientRect().height:document.documentElement.scrollHeight;` +
    `P({type:'resize',height:Math.ceil(h)});}` +
    `window.addEventListener('load',H);` +
    `try{if(typeof ResizeObserver!=='undefined'){var _r=document.getElementById('__veo_root');` +
    `new ResizeObserver(H).observe(_r||document.documentElement);}}catch(e){}` +
    `})();`;

  // CSS del host (todas las hojas de la app) para que el HTML se vea idéntico.
  const hostStyle = opts.hostCss ? `<style>${escapeClosing(opts.hostCss, 'style')}</style>` : '';
  // Tailwind Play CDN (opt-in). El src remoto depende del CSP de la app.
  const tailwind = opts.tailwind ? '<script src="https://cdn.tailwindcss.com"></script>' : '';

  return (
    // Orden: base → CSS del host (utilidades+clases propias+:root) → reset
    // flotante (fondo transparente, sin márgenes) → Tailwind → CSS del autor (gana).
    `<!DOCTYPE html><html ${opts.htmlAttrs}><head><meta charset="utf-8">` +
    '<style>html,body{margin:0;padding:0;}body{font-family:system-ui,-apple-system,sans-serif;}</style>' +
    hostStyle +
    // Reset flotante: el CSS de la app trae reglas de PÁGINA (body{min-height:100vh},
    // html{height:100%}, fondo del layout…) que en un iframe embebido lo estirarían a
    // toda la pantalla (caja negra) en vez de ajustarse al contenido. Neutralizamos
    // tamaño/fondo/overflow (mantenemos color y fuente heredados de la app).
    // `color-scheme:normal` es CLAVE para la transparencia: si el CSS de la app
    // declara `:root{color-scheme:dark}` y no coincide con el del elemento iframe
    // del host (normal), el navegador pinta el canvas OPACO (negro) en vez de
    // transparente — igualamos ambos lados para conservar el fondo transparente.
    '<style>html,body{margin:0!important;padding:0!important;background:transparent!important;' +
    'height:auto!important;min-height:0!important;max-height:none!important;' +
    'width:auto!important;min-width:0!important;overflow:visible!important;' +
    'color-scheme:normal!important;}</style>' +
    tailwind +
    // Inline en <body>: gana incluso sobre reglas `!important` de la app (p. ej.
    // `.dark body{background:#000!important}`) porque el estilo inline tiene la
    // mayor especificidad dentro de la capa important-author.
    `<style>${escapeClosing(css, 'style')}</style></head>` +
    '<body style="margin:0!important;background:transparent!important;min-height:0!important;height:auto!important;">' +
    // Contenedor del contenido: el iframe se dimensiona por SU alto (no el del
    // documento), así el layout de página de la app no lo estira.
    `<div id="__veo_root">${html}</div>` +
    `<script>${bridge}</script>` +
    (js ? `<script>${escapeClosing(js, 'script')}</script>` : '') +
    '</body></html>'
  );
}

function escapeClosing(code: string, tag: 'script' | 'style'): string {
  const rx = tag === 'script' ? /<\/script/gi : /<\/style/gi;
  return code.replace(rx, `<\\/${tag}`);
}
